import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiArrowRight } from "react-icons/fi";
import products from "../data/products";
import services from "../data/services";
import facilityFront from "../assets/photos/facility-front.jpeg";
import cuminImg from "../assets/products/cumin.webp";
import corianderImg from "../assets/products/coriander.jpg";
import "./Home.css";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Home() {
  return (
    <motion.main
      className="page home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* ========== HERO ========== */}
      <section className="hero">
        <div className="hero__grain" aria-hidden="true" />
        <div className="container hero__inner">
          <div className="hero__content">
            <motion.span
              className="hero__eyebrow"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={0}
            >
              <span className="hero__dot" /> Agronexus Trading Pvt. Ltd. · Kutch, Gujarat
            </motion.span>

            <motion.h1
              className="hero__title"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
            >
              From Indian fields <br />
              to the world's <em>finest kitchens.</em>
            </motion.h1>

            <motion.p
              className="hero__lede"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
            >
              Agronexus Trading Pvt. Ltd. is an import–export house
              specialising in premium Indian spices, seeds, and agro
              commodities — cumin, fennel, coriander, sesame, red chilly —
              backed by integrated logistics that move cargo from our Kutch
              facility to destination ports across the world.
            </motion.p>

            <motion.div
              className="hero__cta"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
            >
              <Link to="/products" className="btn btn-primary">
                Explore Products <FiArrowUpRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Request a Quote
              </Link>
            </motion.div>

            <motion.div
              className="hero__meta"
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
            >
              <div className="hero__meta-item">
                <strong>30+</strong>
                <span>Countries shipped</span>
              </div>
              <div className="hero__meta-item">
                <strong>6</strong>
                <span>Core commodities</span>
              </div>
              <div className="hero__meta-item">
                <strong>99.95%</strong>
                <span>Purity available</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="hero__visual"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="hero__visual-card hero__visual-card--1">
              <img
                src={cuminImg}
                alt="Cumin seeds"
              />
              <span>Cumin · Jeera</span>
            </div>
            <div className="hero__visual-card hero__visual-card--2">
              <img
                src={facilityFront}
                alt="Agronexus Trading facility in Kutch"
              />
              <span>Our Facility · Kutch</span>
            </div>
            <div className="hero__visual-card hero__visual-card--3">
              <img
                src={corianderImg}
                alt="Coriander seeds"
              />
              <span>Coriander · Dhaniya</span>
            </div>
            <div className="hero__visual-badge">
              <svg viewBox="0 0 120 120" width="120" height="120">
                <defs>
                  <path
                    id="circle"
                    d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0"
                  />
                </defs>
                <text fontSize="10" letterSpacing="3" fill="currentColor">
                  <textPath href="#circle">
                    FARM TO FREIGHT · FARM TO FREIGHT ·
                  </textPath>
                </text>
              </svg>
              <div className="hero__visual-badge-core">ACW</div>
            </div>
          </motion.div>
        </div>

        {/* Marquee strip */}
        <div className="hero__marquee" aria-hidden="true">
          <div className="hero__marquee-track">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i}>
                Cumin · Fennel · Coriander · Black Sesame · White Sesame · Red
                Chilly · Cumin · Fennel · Coriander · Black Sesame · White
                Sesame · Red Chilly ·{" "}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ABOUT STRIP ========== */}
      <section className="section about-strip">
        <div className="container about-strip__inner">
          <div className="about-strip__left">
            <span className="eyebrow">Who we are</span>
            <h2>
              A trading house that
              <br />
              still thinks like a farmer.
            </h2>
          </div>
          <div className="about-strip__right">
            <p>
              We built Agronexus because the distance between a spice
              farmer in Gujarat and a buyer in Hamburg is not measured in
              kilometres — it's measured in trust. We source directly from
              growing belts we know by name, grade each lot to buyer specs,
              and move it under our own logistics coordination so nothing is
              lost in translation between field and freight.
            </p>
            <Link to="/about" className="inline-link">
              Read our story <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== FEATURED PRODUCTS ========== */}
      <section className="section featured">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Our Range</span>
              <h2>Six commodities. <em>One standard.</em></h2>
            </div>
            <Link to="/products" className="inline-link">
              View all products <FiArrowRight />
            </Link>
          </div>

          <div className="featured__grid">
            {products.map((p, i) => (
              <motion.div
                key={p.slug}
                className="product-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
              >
                <div
                  className="product-card__image"
                  style={{ backgroundColor: p.color + "20" }}
                >
                  <img src={p.image} alt={p.name} />
                </div>
                <div className="product-card__body">
                  <div className="product-card__head">
                    <h3>{p.name}</h3>
                    <span className="product-card__local">{p.local}</span>
                  </div>
                  <p>{p.tagline}</p>
                  <Link to="/products" className="product-card__link">
                    Details <FiArrowUpRight />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SERVICES ========== */}
      <section className="section services-home">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">What we do</span>
              <h2>
                Trade & logistics, <em>under one roof.</em>
              </h2>
            </div>
            <Link to="/services" className="inline-link">
              All services <FiArrowRight />
            </Link>
          </div>

          <div className="services-home__grid">
            {services.slice(0, 6).map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  className="service-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: i * 0.05 }}
                >
                  <div className="service-card__icon">
                    <Icon />
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.summary}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== CTA BAND ========== */}
      <section className="cta-band">
        <div className="container cta-band__inner">
          <h2>
            Have a buyer brief or a shipment plan? <br />
            <em>Let's talk specifics.</em>
          </h2>
          <Link to="/contact" className="btn btn-primary">
            Start a conversation <FiArrowUpRight />
          </Link>
        </div>
      </section>
    </motion.main>
  );
}

export default Home;
